"use client";

import { useCallback, useEffect, useState, useRef } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useScrollAnchor } from "@utils/scrollAnchor";
import AbisLogoIcon from "@components/ui/AbisLogoIcon";
import LanguageSwitcher from "@components/ui/LanguageSwitcher";
import SpeakerAvatars from "@components/ui/SpeakerAvatars";
import { isExternalLinkAvailable } from "@utils/externalLinks";
import navigationDataDe from "@data/config/navigation.json";
import navigationDataEn from "@data/config/navigation.en.json";
import promoData from "@data/components/ki-kurs-promo.json";
import { formatCourseDateBadge, getNextCourseDate } from "@utils/kiKursSchedule";
import { getLocaleFromPathname } from "@utils/i18n/config";
import "@assets/styles/header.scss";

/* A menu item renders as a two-level flyout (category rail + content panel)
   when every child is a group with its own entries. */
const isTwoLevelMenu = (item) =>
  Array.isArray(item?.submenu) &&
  item.submenu.length > 0 &&
  item.submenu.every(
    (group) => Array.isArray(group.submenu) && group.submenu.length > 0
  );

const ChevronRightIcon = () => (
  <svg
    className="nav-flyout__chevron"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M9 6l6 6-6 6" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg
    className="mobile-nav__chevron"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const HeaderPublic = () => {
  const pathname = usePathname();
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [hoveredMenuItem, setHoveredMenuItem] = useState(null);
  const [activeGroupId, setActiveGroupId] = useState(null);
  // Mobile accordion: which category panels are expanded. Seeded from the
  // current route so the section the visitor is in is already open.
  const [openMobileGroups, setOpenMobileGroups] = useState(() => new Set());
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  const cardRef = useRef(null);
  const navListRef = useRef(null);

  // Use global scroll anchor utility
  const { getPageActiveClasses } = useScrollAnchor();

  // Optimierter Scroll-Handler mit RAF - Modal-aware
  useEffect(() => {
    let ticking = false;
    let mutationObserver = null;

    const updateScrollState = () => {
      // Get actual scroll position, even when body is fixed (modal open)
      let scrollY = window.scrollY;

      // If body is fixed (modal is open), get scroll position from body top
      if (document.body.style.position === "fixed" && document.body.style.top) {
        scrollY = Math.abs(parseInt(document.body.style.top) || 0);
      }

      const threshold = 50;

      // React skips the re-render when the value is unchanged, so no
      // comparison against closed-over state is needed — which also lets the
      // listeners below be registered once instead of on every flip.
      setIsScrolled(scrollY > threshold);

      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateScrollState);
        ticking = true;
      }
    };

    const onResize = () => {
      const isPhone = window.innerWidth <= 768;
      const isTabletPortrait =
        window.innerWidth <= 1024 &&
        window.matchMedia("(orientation: portrait)").matches;

      // Use compact header controls on small screens and portrait tablets.
      setIsMobile(isPhone || isTabletPortrait);
    };

    // Watch for body style changes (modal opening/closing)
    mutationObserver = new MutationObserver(() => {
      updateScrollState();
    });

    mutationObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ["style"],
    });

    // Initial checks
    updateScrollState();
    onResize();

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      if (mutationObserver) {
        mutationObserver.disconnect();
      }
    };
  }, []);


  // Keeps the flyout flush with the bottom edge of the header card. The card is
  // roomy at the top of the page and compact once scrolled, so the drop from the
  // nav row down to the card edge is measured instead of hard-coded — and the
  // remaining viewport height is handed to the panel so it can never run past
  // the fold.
  const syncFlyoutMetrics = useCallback(() => {
    const card = cardRef.current;
    const list = navListRef.current;
    if (!card || !list) return;

    const cardRect = card.getBoundingClientRect();
    const listRect = list.getBoundingClientRect();
    // Nav row is display:none on mobile — nothing to anchor to.
    if (!listRect.height) return;

    const offset = Math.max(0, Math.round(cardRect.bottom - listRect.bottom));
    const available = Math.round(window.innerHeight - cardRect.bottom - 24);

    card.style.setProperty("--nav-flyout-offset", `${offset}px`);
    card.style.setProperty("--nav-flyout-max-height", `${Math.max(240, available)}px`);

    // Clamp each flyout to the header card's right edge so it keeps the same
    // 1.25rem page inset as the bar, instead of running to the viewport.
    list.querySelectorAll(":scope > .nav-item").forEach((item) => {
      const itemRect = item.getBoundingClientRect();
      const maxWidth = Math.max(240, Math.round(cardRect.right - itemRect.left));
      item.style.setProperty("--nav-flyout-max-width", `${maxWidth}px`);
    });
  }, []);

  useEffect(() => {
    const card = cardRef.current;
    const list = navListRef.current;
    if (!card || !list) return;

    syncFlyoutMetrics();

    // Web fonts and the CTA buttons settle after first paint, and the card
    // changes height when it goes compact — both move the anchor. The card's
    // padding is animated, so the last word belongs to transitionend.
    const observer = new ResizeObserver(syncFlyoutMetrics);
    observer.observe(card);
    observer.observe(list);
    window.addEventListener("resize", syncFlyoutMetrics);
    card.addEventListener("transitionend", syncFlyoutMetrics);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", syncFlyoutMetrics);
      card.removeEventListener("transitionend", syncFlyoutMetrics);
    };
  }, [isScrolled, isMobile, syncFlyoutMetrics]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuOpen &&
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target) &&
        !buttonRef.current.contains(event.target)
      ) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside, {
        passive: true,
      });

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("touchstart", handleClickOutside);
      };
    }
  }, [menuOpen]);

  const toggleMenu = () => setMenuOpen(!menuOpen);
  const closeMenu = () => setMenuOpen(false);

  const toggleMobileGroup = (groupId) => {
    setOpenMobileGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  // The group whose entries the flyout shows first: the one holding the current
  // page, otherwise the first category.
  const resolveDefaultGroupId = (item) => {
    const match = item.submenu.find((group) =>
      group.submenu.some(
        (entry) =>
          pathname === entry.href || pathname.startsWith(`${entry.href}/`)
      )
    );
    return (match || item.submenu[0]).id;
  };

  const handleMenuEnter = (item) => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    // Anchor is re-measured on open so the panel is always flush, whatever the
    // header was doing beforehand.
    syncFlyoutMetrics();
    // Only on the way in — re-entering from the trigger into the open flyout
    // must not discard the category the visitor is pointing at.
    if (hoveredMenuItem !== item.id && isTwoLevelMenu(item)) {
      setActiveGroupId(resolveDefaultGroupId(item));
    }
    setHoveredMenuItem(item.id);
  };

  const handleMenuLeave = () => {
    // Lazy close: a generous grace period so briefly overshooting the panel
    // edge (or cutting a corner on the way in) doesn't dismiss the flyout —
    // re-entering trigger or panel cancels the timer in handleMenuEnter.
    hoverTimeoutRef.current = setTimeout(() => setHoveredMenuItem(null), 320);
  };

  // Desktop keeps its full navigation while scrolling — the card only gets
  // compact. Collapsing it into a hamburger put the flyout out of reach on
  // every part of the page below the hero.
  const showMenuItems = !isMobile;
  const showDesktopButtons = !isMobile;
  const showScrolledButtons = isMobile;
  const isMeetPage = pathname === "/meet";

  const currentLocale = getLocaleFromPathname(pathname);
  const navigationData = currentLocale === "en" ? navigationDataEn : navigationDataDe;

  // The logo is one real link home in every header state. It used to turn into
  // a scroll-to-top button once the page was scrolled, so on any subpage the
  // first click only scrolled and the visitor had to click again to get home.
  // On the home page itself a navigation to its own URL would just re-fetch
  // the page, so that click scrolls to the top instead.
  const homeHref = currentLocale === "en" ? "/en" : "/";
  const isOnHomePage = pathname === homeHref;
  const handleLogoClick = (event) => {
    closeMenu();
    if (!isOnHomePage) return;
    // Modifier clicks (new tab / window) keep their native behaviour.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
      return;
    }
    event.preventDefault();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const { menuItems, buttons } = navigationData;
  const kursDateBadge = formatCourseDateBadge(getNextCourseDate().date);
  const showKursPromo = currentLocale === "de";

  // Opening the drawer expands the category holding the current page, so the
  // visitor lands on their own context instead of a wall of collapsed rows.
  useEffect(() => {
    if (!menuOpen) return;
    const matches = new Set();
    menuItems.forEach((item) => {
      if (!Array.isArray(item.submenu)) return;
      item.submenu.forEach((group) => {
        if (!Array.isArray(group.submenu)) return;
        const holdsCurrentPage = group.submenu.some(
          (entry) =>
            pathname === entry.href || pathname.startsWith(`${entry.href}/`)
        );
        if (holdsCurrentPage) matches.add(group.id);
      });
    });
    setOpenMobileGroups(matches);
  }, [menuOpen, pathname, menuItems]);

  // Like getPageActiveClasses but won't activate a parent route when another
  // top-level menu item is a more specific match for the current pathname.
  // Example: /services/ki-governance-agentic-bi should NOT activate /services.
  const getTopLevelActiveClasses = (itemHref, baseClasses, activeClasses) => {
    const isExactMatch = pathname === itemHref;
    const isPrefixMatch = pathname.startsWith(`${itemHref}/`);

    let isActive = isExactMatch || isPrefixMatch;

    if (isPrefixMatch && !isExactMatch) {
      const hasBetterMatch = menuItems.some(
        (other) =>
          other.href !== itemHref &&
          (pathname === other.href || pathname.startsWith(`${other.href}/`))
      );
      if (hasBetterMatch) isActive = false;
    }

    return `${baseClasses} ${isActive ? activeClasses : ""}`;
  };

  return (
    <header className="header-wrapper">
      <div
        className={`header-container ${
          isScrolled ? "header-container--scrolled" : ""
        }`}
      >
        <div
          ref={cardRef}
          className={`header-card ${
            isScrolled ? "header-card--scrolled" : ""
          } ${menuOpen ? "header-card--menu-open" : ""}`}
        >
          <nav
            className="flex items-center justify-between w-full"
            role="navigation"
            aria-label="Hauptnavigation"
          >
            <div className="flex items-center gap-3">
              <Link
                href={homeHref}
                onClick={handleLogoClick}
                className="nav-logo"
                aria-label={
                  isOnHomePage
                    ? "Zum Seitenanfang scrollen"
                    : "DBI Analytics – Microsoft Data & AI Beratung, zur Startseite"
                }
                style={{ border: 'none', padding: 0, cursor: 'pointer', display: 'block' }}
              >
                <span className="sr-only">
                  DBI Analytics – Microsoft Data & AI Beratung, zur Startseite
                </span>
              </Link>
            </div>

            <div className="flex items-center gap-5">
              {/* Desktop Navigation Links - Always rendered, controlled by CSS */}
              <ul
                ref={navListRef}
                className={`hidden md:flex items-center gap-5 list-none m-0 p-0 nav-links-container ${
                  showMenuItems ? "nav-links-container--visible" : "nav-links-container--hidden"
                }`}
              >
                {menuItems.map((item) => (
                  <li
                    key={item.id}
                    className="relative nav-item"
                    onMouseEnter={() => item.submenu && handleMenuEnter(item)}
                    onMouseLeave={handleMenuLeave}
                  >
                    <Link
                      href={item.href}
                      className={`${getTopLevelActiveClasses(
                        item.href,
                        "nav-link",
                        "nav-link nav-link--active"
                      )} nav-link--emphasized`}
                    >
                      {item.label}
                    </Link>
                    {item.submenu &&
                      hoveredMenuItem === item.id &&
                      isTwoLevelMenu(item) && (
                        <div
                          ref={dropdownRef}
                          className="nav-dropdown nav-dropdown--twolevel"
                          onMouseEnter={() => handleMenuEnter(item)}
                          onMouseLeave={handleMenuLeave}
                        >
                          {/* Level 1 — category rail */}
                          <ul className="nav-flyout__rail">
                            {item.submenu.map((group) => (
                              <li key={group.id}>
                                <button
                                  type="button"
                                  className={`nav-flyout__cat ${
                                    activeGroupId === group.id
                                      ? "nav-flyout__cat--active"
                                      : ""
                                  }`}
                                  onMouseEnter={() => setActiveGroupId(group.id)}
                                  onFocus={() => setActiveGroupId(group.id)}
                                  aria-pressed={activeGroupId === group.id}
                                >
                                  <span className="nav-flyout__cat-text">
                                    <span className="nav-flyout__cat-label">
                                      {group.label}
                                    </span>
                                    {group.hint && (
                                      <span className="nav-flyout__cat-hint">
                                        {group.hint}
                                      </span>
                                    )}
                                  </span>
                                  <ChevronRightIcon />
                                </button>
                              </li>
                            ))}
                            <li className="nav-flyout__rail-footer">
                              <Link href={item.href} className="nav-flyout__all">
                                {item.overviewLabel || item.label}
                                <span aria-hidden="true">→</span>
                              </Link>
                            </li>
                          </ul>

                          {/* Level 2 — entries of the active category */}
                          <div className="nav-flyout__panel">
                            {item.submenu
                              .filter((group) => group.id === activeGroupId)
                              .map((group) => (
                                <div key={group.id}>
                                  <p className="nav-flyout__panel-label">
                                    {group.label} · {group.submenu.length}{" "}
                                    {group.countLabel || "Leistungen"}
                                  </p>
                                  <ul className="nav-flyout__list">
                                    {group.submenu.map((entry) => (
                                      <li key={entry.id}>
                                        <Link
                                          href={entry.href}
                                          className={`${getPageActiveClasses(
                                            entry.href,
                                            "nav-flyout__entry",
                                            "nav-flyout__entry nav-flyout__entry--active"
                                          )}${entry.featured ? " nav-flyout__entry--featured" : ""}`}
                                        >
                                          <span className="nav-flyout__entry-title">
                                            {entry.label}
                                            {entry.featured && (
                                              <span className="nav-flyout__entry-badge">Live</span>
                                            )}
                                          </span>
                                          {entry.description && (
                                            <span className="nav-flyout__entry-desc">
                                              {entry.description}
                                            </span>
                                          )}
                                        </Link>
                                      </li>
                                    ))}
                                  </ul>
                                  {/* Categories that have their own landing page
                                      (e.g. Success Stories) keep it reachable
                                      from inside the panel. */}
                                  {group.href && (
                                    <Link
                                      href={group.href}
                                      className="nav-flyout__all nav-flyout__all--panel"
                                    >
                                      {group.overviewLabel ||
                                        `Alle ${group.label}`}
                                      <span aria-hidden="true">→</span>
                                    </Link>
                                  )}
                                </div>
                              ))}
                          </div>
                          {showKursPromo && (
                            <Link href={promoData.href} className="nav-flyout__promo">
                              <SpeakerAvatars speakers={promoData.speakers} size={40} />
                              <span className="nav-flyout__promo-copy">
                                <span className="nav-flyout__promo-badge">
                                  {promoData.flyoutPromo.badge} {kursDateBadge}
                                </span>
                                <span className="nav-flyout__promo-title">
                                  {promoData.flyoutPromo.title}
                                </span>
                                <span className="nav-flyout__promo-desc">
                                  {promoData.flyoutPromo.description}
                                </span>
                              </span>
                              <span className="nav-flyout__promo-cta">
                                {promoData.flyoutPromo.cta}
                                <span aria-hidden="true">→</span>
                              </span>
                            </Link>
                          )}
                        </div>
                      )}
                    {item.submenu &&
                      hoveredMenuItem === item.id &&
                      !isTwoLevelMenu(item) && (
                      <ul
                        className={`nav-dropdown ${item.submenu.some(s => Array.isArray(s.submenu) && s.submenu.length > 0) ? "nav-dropdown--mega" : ""}`}
                        onMouseEnter={() => handleMenuEnter(item)}
                        onMouseLeave={handleMenuLeave}
                      >
                        {item.submenu.map((subItem) =>
                          Array.isArray(subItem.submenu) &&
                          subItem.submenu.length > 0 ? (
                            <li key={subItem.id} className="nav-dropdown__group">
                              <span className="nav-dropdown__group-label">
                                {subItem.label}
                              </span>
                              <ul className="nav-dropdown__sublist">
                                {subItem.submenu.map((nested) => (
                                  <li key={nested.id}>
                                    <Link
                                      href={nested.href}
                                      className={getPageActiveClasses(
                                        nested.href,
                                        "nav-dropdown__nested-link",
                                        "nav-dropdown__nested-link nav-dropdown__nested-link--active"
                                      )}
                                    >
                                      {nested.label}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </li>
                          ) : (
                            <li key={subItem.id}>
                              <Link
                                href={subItem.href}
                                className={getPageActiveClasses(
                                  subItem.href,
                                  "nav-dropdown__link",
                                  "nav-dropdown__link nav-dropdown__link--active"
                                )}
                              >
                                {subItem.label}
                              </Link>
                            </li>
                          )
                        )}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>

              {/* Desktop Buttons - Always rendered, controlled by CSS */}
              <div
                className={`hidden md:flex items-center gap-3 desktop-buttons-container ${
                  showDesktopButtons
                    ? "desktop-buttons-container--visible"
                    : "desktop-buttons-container--hidden"
                }`}
              >
                <LanguageSwitcher />
                {/* Hidden while the target host is unreachable — see
                    src/data/config/external-links.json. Internal hrefs (the DE
                    nav points at /services/power-bi-mcp-server) always pass. */}
                {(!buttons.agenticBI.href.startsWith("http") ||
                  isExternalLinkAvailable(buttons.agenticBI.href)) && (
                <Link
                  href={buttons.agenticBI.href}
                  className="btn-nav-ghost"
                  title={buttons.agenticBI.title}
                  aria-label={buttons.agenticBI.ariaLabel}
                  target={buttons.agenticBI.target}
                  rel={buttons.agenticBI.rel}
                >
                  <AbisLogoIcon size="md" />
                  <span>{buttons.agenticBI.label}</span>
                </Link>
                )}
                <Link
                  href={buttons.calendly.href}
                  className={`btn-glow ${isMeetPage ? "opacity-60 cursor-not-allowed" : ""}`}
                  title={isMeetPage ? "Sie befinden sich bereits auf der Meet-Seite" : buttons.calendly.title}
                  aria-disabled={isMeetPage ? "true" : undefined}
                  tabIndex={isMeetPage ? -1 : undefined}
                  target={buttons.calendly.target}
                  rel={buttons.calendly.rel}
                  onClick={
                    isMeetPage
                      ? (event) => {
                          event.preventDefault();
                        }
                      : undefined
                  }
                >
                  <span>{buttons.calendly.label}</span>
                  <span aria-hidden="true">→</span>
                </Link>
              </div>

              {/* Scrolled Buttons - Always rendered, controlled by CSS */}
              <div
                className={`scrolled-buttons-container ${
                  showScrolledButtons
                    ? "scrolled-buttons-container--visible"
                    : "scrolled-buttons-container--hidden"
                }`}
              >
                {/* VIP Button - Only Logo, before Hamburger */}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                    closeMenu();
                  }}
                  className="btn btn-hero btn-vip flex items-center justify-center group"
                  title="Zum Seitenanfang scrollen"
                  aria-label="Zum Seitenanfang scrollen"
                >
                  <AbisLogoIcon
                    size="md"
                    className="transition-transform group-hover:scale-110"
                  />
                </button>

                <button
                  ref={buttonRef}
                  onClick={toggleMenu}
                  aria-label={menuOpen ? "Menü schließen" : "Menü öffnen"}
                  aria-expanded={menuOpen}
                  className={`menu-toggle ${
                    menuOpen ? "menu-toggle--active" : ""
                  }`}
                >
                  <span className="menu-toggle__icon" />
                </button>
              </div>
            </div>
          </nav>
        </div>

        {/* Mobile Menu - Always rendered, controlled by CSS */}
        <div
          ref={menuRef}
          className={`mobile-menu mx-0 ml-auto ${
            menuOpen ? "mobile-menu--open" : ""
          } ${
            showScrolledButtons
              ? "mobile-menu--visible"
              : "mobile-menu--hidden"
          }`}
          aria-hidden={!menuOpen || !showScrolledButtons}
        >
            <div className="mobile-nav">
              {showKursPromo && (
                <Link
                  href={promoData.href}
                  onClick={closeMenu}
                  className="mobile-nav__promo"
                >
                  <SpeakerAvatars speakers={promoData.speakers} size={44} />
                  <span className="mobile-nav__promo-copy">
                    <span className="mobile-nav__promo-badge">
                      {promoData.flyoutPromo.badge} {kursDateBadge}
                    </span>
                    <span className="mobile-nav__promo-title">
                      {promoData.flyoutPromo.title}
                    </span>
                    <span className="mobile-nav__promo-desc">
                      {promoData.flyoutPromo.description}
                    </span>
                  </span>
                </Link>
              )}
              {menuItems.map((item) => {
                const groups = Array.isArray(item.submenu) ? item.submenu : [];

                /* Categories become accordion rows — same labels, hints and
                   entry descriptions the desktop flyout shows, stacked instead
                   of side by side. */
                if (isTwoLevelMenu(item)) {
                  return (
                    <section key={item.id} className="mobile-nav__section">
                      <div className="mobile-nav__section-head">
                        <span className="mobile-nav__section-title">
                          {item.label}
                        </span>
                        <Link
                          href={item.href}
                          onClick={closeMenu}
                          className="mobile-nav__all"
                        >
                          {item.overviewLabel || item.label}
                          <span aria-hidden="true">→</span>
                        </Link>
                      </div>

                      {groups.map((group) => {
                        const isGroupOpen = openMobileGroups.has(group.id);
                        const panelId = `mobile-nav-panel-${group.id}`;
                        return (
                          <div
                            key={group.id}
                            className={`mobile-nav__group ${
                              isGroupOpen ? "mobile-nav__group--open" : ""
                            }`}
                          >
                            <button
                              type="button"
                              className="mobile-nav__cat"
                              onClick={() => toggleMobileGroup(group.id)}
                              aria-expanded={isGroupOpen}
                              aria-controls={panelId}
                            >
                              <span className="mobile-nav__cat-text">
                                <span className="mobile-nav__cat-label">
                                  {group.label}
                                </span>
                                {group.hint && (
                                  <span className="mobile-nav__cat-hint">
                                    {group.hint}
                                  </span>
                                )}
                              </span>
                              <span className="mobile-nav__cat-meta">
                                <span className="mobile-nav__cat-count">
                                  {group.submenu.length}
                                </span>
                                <ChevronDownIcon />
                              </span>
                            </button>

                            {/* Always in the DOM (hidden via CSS when closed)
                                so the deep service links are crawlable in the
                                server HTML of every page. */}
                            <ul id={panelId} className="mobile-nav__list">
                                {group.submenu.map((entry) => (
                                  <li key={entry.id}>
                                    <Link
                                      href={entry.href}
                                      onClick={closeMenu}
                                      className={`${getPageActiveClasses(
                                        entry.href,
                                        "mobile-nav__entry",
                                        "mobile-nav__entry mobile-nav__entry--active"
                                      )}${entry.featured ? " mobile-nav__entry--featured" : ""}`}
                                    >
                                      <span className="mobile-nav__entry-title">
                                        {entry.label}
                                        {entry.featured && (
                                          <span className="nav-flyout__entry-badge">Live</span>
                                        )}
                                      </span>
                                      {entry.description && (
                                        <span className="mobile-nav__entry-desc">
                                          {entry.description}
                                        </span>
                                      )}
                                    </Link>
                                  </li>
                                ))}
                                {group.href && (
                                  <li>
                                    <Link
                                      href={group.href}
                                      onClick={closeMenu}
                                      className="mobile-nav__all mobile-nav__all--panel"
                                    >
                                      {group.overviewLabel ||
                                        `Alle ${group.label}`}
                                      <span aria-hidden="true">→</span>
                                    </Link>
                                  </li>
                                )}
                              </ul>
                          </div>
                        );
                      })}
                    </section>
                  );
                }

                /* Flat submenu — rendered as a labelled block of links. */
                if (groups.length > 0) {
                  return (
                    <section key={item.id} className="mobile-nav__section">
                      <div className="mobile-nav__section-head">
                        <span className="mobile-nav__section-title">
                          {item.label}
                        </span>
                      </div>
                      <ul className="mobile-nav__list">
                        {groups.map((subItem) => (
                          <li key={subItem.id}>
                            <Link
                              href={subItem.href}
                              onClick={closeMenu}
                              className={getPageActiveClasses(
                                subItem.href,
                                "mobile-nav__entry",
                                "mobile-nav__entry mobile-nav__entry--active"
                              )}
                            >
                              <span className="mobile-nav__entry-title">
                                {subItem.label}
                              </span>
                              {subItem.description && (
                                <span className="mobile-nav__entry-desc">
                                  {subItem.description}
                                </span>
                              )}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </section>
                  );
                }

                return (
                  <Link
                    key={item.id}
                    href={item.href}
                    onClick={closeMenu}
                    className={`${getTopLevelActiveClasses(
                      item.href,
                      "mobile-nav__link",
                      "mobile-nav__link mobile-nav__link--active"
                    )}`}
                  >
                    {item.label}
                  </Link>
                );
              })}

              <div className="mobile-nav__footer">
                <LanguageSwitcher />
                {(!buttons.agenticBI.href.startsWith("http") ||
                  isExternalLinkAvailable(buttons.agenticBI.href)) && (
                <Link
                  href={buttons.agenticBI.href}
                  className="btn-nav-ghost w-full justify-center"
                  onClick={closeMenu}
                  title={buttons.agenticBI.title}
                  target={buttons.agenticBI.target}
                  rel={buttons.agenticBI.rel}
                >
                  <AbisLogoIcon size="md" />
                  <span>{buttons.agenticBI.mobileLabel}</span>
                </Link>
                )}
                <Link
                  href={buttons.calendly.href}
                  className={`btn-glow w-full justify-center ${isMeetPage ? "opacity-60 cursor-not-allowed" : ""}`}
                  aria-disabled={isMeetPage ? "true" : undefined}
                  tabIndex={isMeetPage ? -1 : undefined}
                  target={buttons.calendly.target}
                  rel={buttons.calendly.rel}
                  onClick={(event) => {
                    if (isMeetPage) {
                      event.preventDefault();
                      return;
                    }
                    closeMenu();
                  }}
                >
                  <span>{buttons.calendly.label}</span>
                  <span aria-hidden="true">→</span>
                </Link>
              </div>
            </div>
          </div>
      </div>
    </header>
  );
};

export default HeaderPublic;
